import React, { useState, useEffect } from 'react'
import {
  STRIPE_PRIMARY,
  STRIPE_TEXT_PRIMARY,
  STRIPE_TEXT_SECONDARY,
} from 'constants/stripe'
import { sharedStyles } from './shared-styles'
import { Organization } from 'interfaces/organization'

interface FreeTrialDaysProps {
  onTrialDaysChange?: (days: number | null) => void
  initialTrialDays?: number | null
  org?: Organization
}

const FreeTrialDays: React.FC<FreeTrialDaysProps> = ({
  onTrialDaysChange,
  initialTrialDays = null,
  org,
}) => {
  const [trialDays, setTrialDays] = useState(initialTrialDays || 0)
  const [remainingDays, setRemainingDays] = useState<number | null>(null)
  const [newTrialEndDate, setNewTrialEndDate] = useState<string | null>(null)
  const [selectedDate, setSelectedDate] = useState<string>('')
  const [showTrialInputs, setShowTrialInputs] = useState(
    initialTrialDays !== null && initialTrialDays > 0
  )

  // Initialize trial days based on existing trial or initialTrialDays
  useEffect(() => {
    let daysToSet = 0

    // Check if there are initial trial days
    if (initialTrialDays !== null && initialTrialDays >= 0) {
      daysToSet = initialTrialDays
    }
    // Check if there's an existing trial period
    else if (
      org?.organization_subscription?.subscription_status === 'trialing' &&
      org?.organization_subscription?.trial_end
    ) {
      const trialEnd = new Date(org.organization_subscription.trial_end)
      const now = new Date()

      // Reset both dates to midnight for accurate day calculation
      const trialEndDate = new Date(
        trialEnd.getFullYear(),
        trialEnd.getMonth(),
        trialEnd.getDate()
      )
      const currentDate = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate()
      )

      const remaining = Math.floor(
        (trialEndDate.getTime() - currentDate.getTime()) / (1000 * 60 * 60 * 24)
      )

      if (remaining > 0) {
        daysToSet = remaining
        setRemainingDays(remaining)
      }
    }

    // Update state
    setTrialDays(daysToSet)

    // Notify parent if we're setting trial days
    if (initialTrialDays === null) {
      onTrialDaysChange?.(daysToSet)
    }
  }, [org, initialTrialDays, onTrialDaysChange])

  // Calculate new trial end date when trial days change
  useEffect(() => {
    if (trialDays > 0) {
      // Calculate new trial end date based on current trial days
      const now = new Date()
      const newEndDate = new Date(
        now.getTime() + trialDays * 24 * 60 * 60 * 1000
      )
      setNewTrialEndDate(newEndDate.toISOString())
      setSelectedDate(newEndDate.toISOString().split('T')[0]) // Format as YYYY-MM-DD for date input
    } else {
      setNewTrialEndDate(null)
      setSelectedDate('')
    }
  }, [trialDays])

  // Handle date picker change
  const handleDateChange = (dateString: string) => {
    if (!dateString) return

    const selectedDate = new Date(dateString)
    const now = new Date()

    // Reset both dates to midnight for accurate day calculation
    const selectedDateAtMidnight = new Date(
      selectedDate.getFullYear(),
      selectedDate.getMonth(),
      selectedDate.getDate()
    )
    const currentDateAtMidnight = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate()
    )

    const daysDiff = Math.floor(
      (selectedDateAtMidnight.getTime() - currentDateAtMidnight.getTime()) /
        (1000 * 60 * 60 * 24)
    )

    if (daysDiff >= 0) {
      setTrialDays(daysDiff)
      onTrialDaysChange?.(daysDiff)
    }
  }

  const handleDaysChange = (days: number) => {
    setTrialDays(days)
    onTrialDaysChange?.(days)
  }

  const handleAddTrialDays = () => {
    setShowTrialInputs(true)
    setTrialDays(0)
    onTrialDaysChange?.(0)
  }

  return (
    <div style={sharedStyles.sectionContainer}>
      <h6 style={sharedStyles.sectionHeading}>Free trial days</h6>

      {!showTrialInputs && trialDays === 0 ? (
        <div style={sharedStyles.subSectionContainer}>
          <button
            type="button"
            onClick={handleAddTrialDays}
            style={{
              backgroundColor: STRIPE_PRIMARY,
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              padding: '8px 16px',
              fontSize: '14px',
              cursor: 'pointer',
              fontWeight: '500',
            }}
          >
            Add Trial Days
          </button>
        </div>
      ) : (
        <div style={sharedStyles.subSectionContainer}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '24px',
              flexWrap: 'wrap',
            }}
          >
            {/* Trial period days */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ color: STRIPE_TEXT_SECONDARY, fontSize: '14px' }}>
                Days:
              </span>
              <input
                type="number"
                value={trialDays}
                onChange={(e) => handleDaysChange(Number(e.target.value))}
                style={{
                  ...sharedStyles.textInput,
                  width: '80px',
                  textAlign: 'center',
                }}
                min="0"
                max="365"
              />
            </div>

            {/* Date picker - show when trial days >= 0 */}
            {trialDays >= 0 && (
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                <span
                  style={{ color: STRIPE_TEXT_SECONDARY, fontSize: '14px' }}
                >
                  End:
                </span>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => handleDateChange(e.target.value)}
                  style={{
                    ...sharedStyles.textInput,
                    width: '140px',
                    padding: '6px 8px',
                  }}
                  min={new Date().toISOString().split('T')[0]} // Can't select past dates
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default FreeTrialDays
